import { tr } from '../../../i18n/i18n';
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import UserAvatar from '../../atoms/UserAvatar';
import { APP_ROUTES } from '../../../constants';
import {
  fetchGuardianStudents,
  selectOrganizationByStudent,
} from '../../../redux/action/auth.action';
import {
  resetAuthState,
  setSelectedStudent,
  selectGuardianStudents,
  selectGuardianChildren,
  selectSelectedStudent,
} from '../../../redux/slices/auth.slice';
import { showNotification } from '../../../redux/slices/notification.slice';
import { useAppDispatch, useAppSelector } from '../../../redux/stateHooks';
import { finalizeAuthSession } from '../../../utility/authSession';
import { tokenManager } from '../../../utility/tokenManager';
import type {
  IGuardianStudent,
  IGuardianStudentOrganization,
} from '../../../interface/auth.interface';

interface GuardianStudentSelectorProps {
  isExpanded: boolean;
}

type StudentOption = IGuardianStudent & { disabled: boolean };
type OrganizationGroup = Omit<IGuardianStudentOrganization, 'students'> & {
  students: StudentOption[];
};

/** Gap between the sidebar's right edge and the flyout panel */
const PANEL_GAP_PX = 8;

/**
 * GuardianStudentSelector — a normal sidebar bottom item (styled like
 * "Change Password") for guardians (role 'Parent'). Clicking it opens an
 * on-theme flyout panel to the RIGHT of the sidebar listing the guardian's
 * students grouped under clearly separated organization sections (the CURRENT
 * organization context is badged).
 *
 * Selection mirrors the mobile app:
 * - Student in the CURRENT organization (org id decoded from the access
 *   token): local selection only — update `selectedStudent` in the slice,
 *   NO API call.
 * - Student in a DIFFERENT organization: POST /auth/selectOrganizationByStudent
 *   → new org-scoped accessToken/refreshToken (response headers) + flattened
 *   auth body ({ status, message, role, profile }) applied to state, then the
 *   shared session plumbing (privileges/menu/org logo/top bar) runs via
 *   finalizeAuthSession.
 */
const GuardianStudentSelector: React.FC<GuardianStudentSelectorProps> = ({ isExpanded }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const guardianStudents = useAppSelector(selectGuardianStudents);
  const children = useAppSelector(selectGuardianChildren);
  const selectedStudent = useAppSelector(selectSelectedStudent);
  const currentOrganization = useAppSelector((state) => state.auth.organization);

  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [switchingStudentId, setSwitchingStudentId] = useState<string | null>(null);
  const [panelPosition, setPanelPosition] = useState<{ left: number; bottom: number } | null>(null);

  const triggerRef = useRef<HTMLLIElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Organization id of the CURRENT session (decoded from the access token) —
  // decides same-org (local selection) vs cross-org (token exchange).
  const currentOrgId = tokenManager.getOrganizationId();

  // Load ALL students (flat, each carrying its organization) for this guardian
  useEffect(() => {
    setIsLoading(true);
    dispatch(fetchGuardianStudents()).finally(() => setIsLoading(false));
  }, [dispatch]);

  /**
   * Panel data: group the flat /me/students list by each student's embedded
   * organization. Falls back to the current session's children (single group
   * under the current organization) until — or if — GET /me/students resolves.
   */
  const organizationGroups: OrganizationGroup[] = useMemo(() => {
    // Build groups from the flat list, preserving first-seen organization order
    if (guardianStudents && guardianStudents.length > 0) {
      const groups = new Map<string, OrganizationGroup>();
      for (const student of guardianStudents) {
        const org = student.organization;
        const orgId = org?.id || currentOrgId || 'current';
        const orgDisabled = org?.review_status === 'PENDING' || org?.status === false;
        if (!groups.has(orgId)) {
          groups.set(orgId, {
            id: orgId,
            name: org?.name || currentOrganization || 'My Organization',
            review_status: org?.review_status,
            status: org?.status,
            students: [],
          });
        }
        groups.get(orgId)!.students.push({
          ...student,
          // Inactive students and students of pending/inactive orgs are not selectable
          disabled: student.status === false || orgDisabled,
        });
      }
      return Array.from(groups.values());
    }

    // Fallback: the current org-scoped children (no organization metadata)
    if (children && children.length > 0) {
      return [
        {
          id: currentOrgId || 'current',
          name: currentOrganization || 'My Organization',
          students: children.map((student) => ({
            ...student,
            disabled: student.status === false,
          })),
        },
      ];
    }

    return [];
  }, [guardianStudents, children, currentOrgId, currentOrganization]);

  /**
   * Position the flyout just past the sidebar's right edge, bottom-aligned to
   * the trigger row. `position: fixed` + portal to body so it works for both
   * the expanded (w-72) and collapsed (w-20) sidebar widths.
   */
  useLayoutEffect(() => {
    if (!isOpen) return undefined;

    const updatePosition = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      setPanelPosition({
        left: rect.right + PANEL_GAP_PX,
        bottom: Math.max(PANEL_GAP_PX, window.innerHeight - rect.bottom),
      });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    return () => window.removeEventListener('resize', updatePosition);
  }, [isOpen, isExpanded]);

  // Close on outside click and Escape
  useEffect(() => {
    if (!isOpen) return undefined;

    const handleMouseDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (panelRef.current?.contains(target) || triggerRef.current?.contains(target)) return;
      setIsOpen(false);
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const handlePick = async (org: OrganizationGroup, student: StudentOption) => {
    if (student.disabled || switchingStudentId) return;

    // Same organization as the current session — local selection only, NO
    // API call (mirrors mobile). The token is already scoped to this org.
    if (org.id === currentOrgId) {
      dispatch(setSelectedStudent(student));
      setIsOpen(false);
      return;
    }

    // Different organization — exchange tokens so the session is re-scoped
    // to the picked student's organization.
    setSwitchingStudentId(student.id);
    try {
      const result = await dispatch(
        selectOrganizationByStudent({ studentId: student.id }),
      ).unwrap();

      // New tokens already stored by the reducer (and selectedStudent is set
      // to the PICKED student there) — refresh privileges, role, org logo and
      // metadata so the sidebar and top bar reflect the new organization.
      await finalizeAuthSession(dispatch, result.response);

      dispatch(
        showNotification({
          message: `Now viewing ${student.display_name} — ${org.name}.`,
          severity: 'success',
        }),
      );
      setIsOpen(false);
    } catch (err: any) {
      const message: string = err?.message || 'Failed to switch student. Please try again.';
      dispatch(showNotification({ message, severity: 'error' }));

      // Backend "No active session found" branch — the session was force
      // logged out server-side; local tokens are dead. Back to login.
      if (message.includes('No active session')) {
        tokenManager.clearAllTokens();
        dispatch(resetAuthState());
        navigate(APP_ROUTES.LOGIN, { replace: true });
      }
    } finally {
      setSwitchingStudentId(null);
    }
  };

  // Nothing to select and nothing loading — hide entirely
  if (!isLoading && organizationGroups.length === 0) return null;

  const triggerLabel = selectedStudent?.display_name || 'Select Student';
  // Organization the selected student belongs to — shown as the trigger's
  // secondary line so the org context is always visible.
  const selectedStudentOrgName = selectedStudent
    ? organizationGroups.find((org) => org.students.some((s) => s.id === selectedStudent.id))
        ?.name ??
      currentOrganization ??
      ''
    : '';
  const triggerSubLabel = selectedStudent
    ? selectedStudentOrgName || 'Tap to switch'
    : 'Choose who to view';
  const triggerIcon = selectedStudent ? (
    <UserAvatar
      name={selectedStudent.display_name || ''}
      image={selectedStudent.upload}
      size={20}
      style={{ marginRight: 0, flexShrink: 0 }}
    />
  ) : (
    <i className="pi pi-users text-base" aria-hidden="true" />
  );

  /** Right-side flyout panel (portal to body, fixed next to the sidebar) */
  const flyoutPanel =
    isOpen && panelPosition
      ? createPortal(
          <div
            ref={panelRef}
            role="dialog"
            aria-label={tr('g.switch_student', 'Switch student')}
            className="fixed z-50 w-72 max-w-[80vw] overflow-y-auto rounded-md border border-gray-100 bg-white p-2 shadow-lg"
            style={{
              left: panelPosition.left,
              bottom: panelPosition.bottom,
              maxHeight: '70vh',
            }}
          >
            <h3 className="font-poppins font-semibold p-2 text-sm text-gray-800">
              {tr('g.switch_student_title', 'Switch Student')}
            </h3>

            {isLoading && organizationGroups.length === 0 ? (
              <div className="flex items-center gap-2 p-3 text-sm text-gray-500">
                <i className="pi pi-spin pi-spinner text-sm" aria-hidden="true" />
                {tr('g.loading_students', 'Loading students...')}
              </div>
            ) : (
              organizationGroups.map((org) => {
                const isCurrentOrg = org.id === currentOrgId;
                return (
                  <div key={org.id} className="mb-1">
                    {/* Organization section header — the org separation cue */}
                    <div className="flex items-center gap-2 border-t border-gray-100 px-2 pt-2 pb-1 first:border-t-0">
                      <i className="pi pi-building text-xs text-gray-400" aria-hidden="true" />
                      <span className="min-w-0 flex-1 truncate text-xs font-semibold uppercase tracking-wide text-gray-500">
                        {org.name}
                      </span>
                      {org.review_status === 'PENDING' && (
                        <span className="flex-shrink-0 text-[10px] text-gray-400">
                          {tr('g.pending_approval_paren', '(Pending approval)')}
                        </span>
                      )}
                      {org.status === false && (
                        <span className="flex-shrink-0 text-[10px] text-gray-400">
                          ({tr('status.inactive', 'Inactive')})
                        </span>
                      )}
                      {isCurrentOrg && (
                        <span className="flex-shrink-0 rounded bg-purple-50 px-1.5 py-0.5 text-[10px] font-semibold text-[#6C4E9A]">
                          {tr('g.current', 'Current')}
                        </span>
                      )}
                    </div>

                    <ul>
                      {org.students.map((student) => {
                        const isSelected = student.id === selectedStudent?.id;
                        const isSwitching = switchingStudentId === student.id;
                        return (
                          <li key={student.id}>
                            <button
                              type="button"
                              disabled={student.disabled || !!switchingStudentId}
                              onClick={() => handlePick(org, student)}
                              className={`flex w-full items-center gap-2 rounded-md p-2 text-left text-sm transition-colors duration-200 ${
                                student.disabled
                                  ? 'cursor-not-allowed text-gray-400'
                                  : isSelected
                                  ? 'bg-purple-50 font-semibold text-[#6C4E9A]'
                                  : 'cursor-pointer text-gray-700 hover:bg-gray-100'
                              }`}
                            >
                              <UserAvatar
                                name={student.display_name || ''}
                                image={student.upload}
                                size={24}
                                style={{
                                  marginRight: 0,
                                  flexShrink: 0,
                                  opacity: student.disabled ? 0.5 : 1,
                                }}
                              />
                              <span className="min-w-0 flex-1 truncate">
                                {student.display_name}
                              </span>
                              {isSwitching && (
                                <i
                                  className="pi pi-spin pi-spinner flex-shrink-0 text-xs"
                                  aria-hidden="true"
                                />
                              )}
                              {!isSwitching && isSelected && (
                                <i
                                  className="pi pi-check flex-shrink-0 text-xs text-[#6C4E9A]"
                                  aria-hidden="true"
                                />
                              )}
                              {!isSwitching && !isSelected && student.status === false && (
                                <span className="flex-shrink-0 text-[10px] text-gray-400">
                                  {tr('status.inactive', 'Inactive')}
                                </span>
                              )}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                );
              })
            )}
          </div>,
          document.body,
        )
      : null;

  // Trigger — a normal sidebar bottom item (matches MenuItem styling)
  if (!isExpanded) {
    return (
      <div className="mb-3 pb-3 border-b border-gray-200">
        <ul className="items-start space-y-1">
          <li ref={triggerRef} className="relative my-2">
            <div
              className={`flex items-center justify-center cursor-pointer p-3 rounded-md transition-colors duration-200 ${
                isOpen ? 'bg-purple-100' : 'hover:bg-gray-100'
              }`}
              onClick={() => setIsOpen((prev) => !prev)}
              role="button"
              aria-haspopup="dialog"
              aria-expanded={isOpen}
              aria-label={triggerLabel}
            >
              {triggerIcon}
            </div>
          </li>
        </ul>
        {flyoutPanel}
      </div>
    );
  }

  return (
    <div className="mb-3 pb-3 border-b border-gray-200">
      <span className="block px-2 mb-1 font-poppins text-[10px] font-semibold uppercase tracking-wide text-gray-400">
        {tr('g.viewing_student', 'Viewing Student')}
      </span>
      <ul className="items-start">
        <li
          ref={triggerRef}
          className={`rounded-lg cursor-pointer transition-colors duration-200 border p-2 ${
            isOpen
              ? 'border-[#6C4E9A] bg-purple-50'
              : 'border-gray-200 bg-white hover:border-[#6C4E9A] hover:bg-purple-50/40'
          }`}
          onClick={() => setIsOpen((prev) => !prev)}
          role="button"
          aria-haspopup="dialog"
          aria-expanded={isOpen}
        >
          <div className="flex items-center gap-2.5">
            <div className="flex-shrink-0">{triggerIcon}</div>
            <div className="min-w-0 flex-grow">
              <span
                className={`block font-poppins text-sm font-medium leading-tight truncate ${
                  isOpen ? 'text-[#6C4E9A]' : 'text-gray-800'
                }`}
              >
                {triggerLabel}
              </span>
              <span className="block font-poppins text-[11px] leading-tight truncate text-gray-400">
                {triggerSubLabel}
              </span>
            </div>
            <i className="pi pi-angle-up text-sm text-gray-400 flex-shrink-0" aria-hidden="true" />
          </div>
        </li>
      </ul>
      {flyoutPanel}
    </div>
  );
};

export default GuardianStudentSelector;
